import React from 'react';

import type { Document } from '@contentful/rich-text-types';
import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import type { ISiteFooter } from 'declarations/contentful';

import FooterAppDownload from './FooterAppDownload';
import FooterBrand from './FooterBrand';
import FooterHelpCenter from './FooterHelpCenter';
import FooterLegal from './FooterLegal';
import FooterNav from './FooterNav';

const DEFAULT_APPLE_STORE_LINK =
  'https://apps.apple.com/us/app/opendoor-buy-sell-homes/id1143708068';
const DEFAULT_GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';
const DEFAULT_HELP_CENTER_LINK = 'https://help.opendoor.com/';

export interface IFooterProps {
  siteFooter?: ISiteFooter;
  extraLegalText?: Document;
  showCustomerTestimonialLegal?: boolean;
  showEligibilityLegal?: boolean;
  showPromotionLegal?: boolean;
  showThePdpCopyrightLegal?: boolean;
}

const FooterSellInYourMarket = ({
  sellInYourMarket,
}: {
  sellInYourMarket?: { label: string; path: string }[];
}) => {
  if (!sellInYourMarket?.length) {
    return null;
  }

  const sortedMarkets = [...sellInYourMarket].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Col className="w-full items-start gap-4 overflow-hidden rounded-xl bg-od-bg-inverse-secondary p-4">
      <Text as="p" className="min-w-20 shrink-0 text-label-sm">
        Learn about selling in your market
      </Text>
      <Row role="list" className="min-w-0 flex-1 flex-wrap items-end gap-2">
        {sortedMarkets.map((market, i) => (
          <React.Fragment key={market.path}>
            <Box role="listitem">
              <Box
                analyticsName={`footer-link-column-${market.label}`}
                href={`/sell/${market.path}`}
                aria-label={market.label}
                className="w-max text-body-md"
              >
                {market.label}
              </Box>
            </Box>
            {i < sortedMarkets.length - 1 && (
              <Text as="span" aria-hidden="true">
                |
              </Text>
            )}
          </React.Fragment>
        ))}
      </Row>
    </Col>
  );
};

export const FooterForegroundBox = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => {
  return (
    <Col className="w-full flex-1 items-center justify-center gap-2 rounded-xl bg-od-bg-inverse-secondary p-4 md:flex-row md:justify-between">
      <Text as="p" className="shrink-0 text-label-sm">
        {title}
      </Text>
      {children}
    </Col>
  );
};

const Footer = ({
  siteFooter,
  extraLegalText,
  showCustomerTestimonialLegal,
  showEligibilityLegal,
  showPromotionLegal,
  showThePdpCopyrightLegal,
}: IFooterProps) => {
  const footerTitle = siteFooter?.fields.footerTitle;
  const footerNav = siteFooter?.fields.footerNav;
  const sellInYourMarket = siteFooter?.fields.sellInYourMarket as
    | { label: string; path: string }[]
    | undefined;
  const googlePlaystoreLink = siteFooter?.fields.googlePlaystoreLink ?? DEFAULT_GOOGLE_PLAY_LINK;
  const appleStoreLink = siteFooter?.fields.appleStoreLink ?? DEFAULT_APPLE_STORE_LINK;
  const helpCenterLink = siteFooter?.fields.helpCenterLink ?? DEFAULT_HELP_CENTER_LINK;
  const locale = siteFooter?.sys.locale;
  const aiPrompt = siteFooter?.fields.aiPrompt;

  const appleStoreImageUrl =
    locale === 'es'
      ? 'https://images.opendoor.com/source/s3/imgdrop-production/2025-06-1750894044413-61865.png?service=cosmos'
      : 'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?service=cosmos';
  const googlePlayStoreImageUrl =
    locale === 'es'
      ? 'https://images.opendoor.com/source/s3/imgdrop-production/2025-06-1750894682317-95550.png?service=cosmos'
      : 'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?service=cosmos';

  return (
    <Col as="footer" className="min-h-113 bg-od-bg-inverse-primary text-od-content-inverse-primary">
      <Box className="block">
        <Box className="mx-auto block w-full max-w-360 p-5 pt-22 md:p-20">
          <Col className="max-w-full gap-16 lg:flex-row">
            <FooterBrand footerTitle={footerTitle} aiPrompt={aiPrompt} />
            <Col className="min-w-0 flex-1 gap-4">
              <FooterNav footerNav={footerNav} />
              <FooterSellInYourMarket sellInYourMarket={sellInYourMarket} />
              <Col className="gap-4 xl:flex-row">
                <FooterAppDownload
                  appleStoreLink={appleStoreLink}
                  appleStoreImageUrl={appleStoreImageUrl}
                  googlePlaystoreLink={googlePlaystoreLink}
                  googlePlayStoreImageUrl={googlePlayStoreImageUrl}
                  locale={locale}
                />
                <FooterHelpCenter helpCenterLink={helpCenterLink} />
              </Col>

              <FooterLegal
                extraLegalText={extraLegalText}
                showCustomerTestimonialLegal={showCustomerTestimonialLegal}
                showEligibilityLegal={showEligibilityLegal}
                showPromotionLegal={showPromotionLegal}
                showThePdpCopyrightLegal={showThePdpCopyrightLegal}
              />

              <Row className="flex-wrap items-center gap-4 pt-16">
                <Text as="span" className="text-body-md">
                  &copy; {new Date().getFullYear()} Opendoor | All rights reserved.
                </Text>
                <Row className="items-center gap-4">
                  <Image
                    src="https://images.opendoor.com/source/s3/imgdrop-production/f2398a1e83a24962a9297d13f2ac6ea5.png?preset=square-2048"
                    alt="Equal Housing Opportunity"
                    width={38}
                    height={38}
                  />
                  <Box
                    href="https://www.bbb.org/us/az/tempe/profile/real-estate-services/opendoor-1126-1000060421/#sealclick"
                    analyticsName="cosmos-landing-page-v2-footer-bbb-link"
                    aria-label="Better Business Bureau accredited since June 8th 2021 link"
                    className="inline-flex"
                  >
                    <Image
                      src="https://images.opendoor.com/source/s3/imgdrop-production/1cd65eee0b8345689ceff0292986f125.png?preset=square-2048"
                      alt="Better Business Bureau"
                      width={105}
                      height={38}
                    />
                  </Box>
                </Row>
              </Row>
            </Col>
          </Col>
        </Box>
      </Box>
    </Col>
  );
};

export default Footer;
